import { useEffect, useId, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import "../../../auth/auth.css";
import { Modal } from "../../../auth/Modal.tsx";
import { StatusBadge, type StatusLevel } from "../../../components/index.ts";
import { OtpInput } from "../../../components/OtpInput.tsx";
import { useOptionalAuth } from "../../../auth/index.ts";
import { useServiceAdmin } from "./use-service-admin.ts";
import {
  CONTROL_POLICY_ROOT,
  POLICY_JWS_LIMIT,
  controlPolicyAction,
  estimateSignedJwsChars,
  policyErrorMessage,
  type AssignmentPreview,
  type AssignmentRow,
  type AssignmentsResponse,
  type CoverageResponse,
  type CoverageRow,
  type PolicyDetailResponse,
  type PolicyFinding,
  type PolicyListResponse,
  type PolicyState,
  type PolicyValidationResponse,
  type PolicyVersionMeta,
  type PolicyVersionResponse,
} from "./control-policy-client.ts";

const POLICY_ID = /^[A-Za-z0-9_.:-]{1,96}$/u;
const EMPTY_VALIDATION: PolicyValidationResponse = {
  ok: false,
  findings: [],
  operation_classes: {},
  acknowledgement_required: false,
  acknowledgement_items: [],
};

function policyPath(path: string): string { return `${CONTROL_POLICY_ROOT}/${path}`; }
function encoded(value: string): string { return encodeURIComponent(value); }
function secondsDate(seconds: number): string {
  const value = new Date(seconds * 1000);
  return Number.isFinite(value.getTime()) ? value.toLocaleString() : "Unknown";
}
function stateLevel(state: PolicyState): StatusLevel {
  if (state === "ok") return "healthy";
  if (state === "expiring") return "warn";
  if (state === "expired") return "error";
  return "unknown";
}
function stateLabel(state: PolicyState): string {
  return state === "none" ? "Draft only" : state[0]!.toUpperCase() + state.slice(1);
}
function isUnavailable(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { status?: unknown }).status === 404;
}
function invalidatePolicies(client: ReturnType<typeof useQueryClient>): Promise<void> {
  return client.invalidateQueries({ queryKey: ["service-admin", "ais", CONTROL_POLICY_ROOT] });
}

function AvailabilityError(props: { readonly error: unknown; readonly area: string }): ReactNode {
  if (isUnavailable(props.error)) {
    return (
      <div className="state-box" data-kind="empty" role="status">
        <strong>Policy administration is unavailable</strong>
        <span>The AIS policy-admin feature is off or unavailable for this tenant. No policy controls are active here.</span>
      </div>
    );
  }
  return (
    <div className="state-box" data-kind="error" role="alert">
      <strong>Could not load {props.area}</strong>
      <span>The AIS policy service could not be reached. No data was changed.</span>
    </div>
  );
}

/** Policy list, direct-id navigation, draft editor, strict publish, and immutable versions. */
export function ControlPolicies(): ReactNode {
  const query = useServiceAdmin<PolicyListResponse>("ais", policyPath("policies"), [CONTROL_POLICY_ROOT, "policies"]);
  const coverage = useServiceAdmin<CoverageResponse>("ais", policyPath("coverage"), [CONTROL_POLICY_ROOT, "coverage"]);
  const isAdmin = useOptionalAuth()?.identity?.role === "admin";
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [directId, setDirectId] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const rows = query.data?.policies ?? [];

  if (query.isError) return <AvailabilityError error={query.error} area="policies" />;
  const expiring = rows.filter((row) => row.state === "expiring").length;
  const expired = rows.filter((row) => row.state === "expired").length;

  const openDirect = (event: FormEvent): void => {
    event.preventDefault();
    const id = directId.trim();
    if (POLICY_ID.test(id)) setSelectedId(id);
  };

  return (
    <div className="svc-admin svc-admin--full policy-admin">
      <div className="svc-adm2__stats" aria-label="Policy summary">
        <Stat label="Policies" value={query.isPending ? "…" : String(rows.length)} />
        <Stat label="Expiring" value={query.isPending ? "…" : String(expiring)} />
        <Stat label="Expired" value={query.isPending ? "…" : String(expired)} />
        <Stat label="Affected agents" value={coverage.isPending ? "…" : String((coverage.data?.noPolicyCount ?? 0) + (coverage.data?.expiringOrExpiredCount ?? 0))} />
      </div>

      <div className="policy-admin__toolbar">
        <form className="policy-admin__direct" onSubmit={openDirect}>
          <label htmlFor="policy-direct-id">Open policy by exact ID</label>
          <input id="policy-direct-id" value={directId} maxLength={96} pattern="[A-Za-z0-9_.:-]+" onChange={(event) => setDirectId(event.target.value)} />
          <button type="submit" className="svc-admin__btn" disabled={!POLICY_ID.test(directId.trim())}>Open policy</button>
        </form>
        <button type="button" className="svc-admin__btn" disabled={!isAdmin} aria-describedby={!isAdmin ? "policy-admin-only" : undefined} onClick={() => setShowCreate((value) => !value)}>
          New policy
        </button>
      </div>
      {!isAdmin && <p id="policy-admin-only" className="svc-admin__muted">You can inspect policies. Only a tenant administrator can create, save, publish, or assign them.</p>}
      {showCreate && isAdmin && <CreatePolicy onCreated={(id) => { setSelectedId(id); setDirectId(id); setShowCreate(false); }} />}

      <div className="svc-admin__split">
        <section className="svc-adm2__card" aria-label="Policy list">
          <div className="svc-adm2__card-head"><h3 className="svc-admin__h">Policies</h3><span className="svc-adm2__card-sub">drafts and published versions</span></div>
          {query.isPending ? (
            <div className="state-box" data-kind="loading" role="status"><strong>Loading policies…</strong><span>Fetching policy summaries.</span></div>
          ) : rows.length === 0 ? (
            <div className="state-box" data-kind="empty"><strong>No policies</strong><span>Create a policy draft to begin.</span></div>
          ) : (
            <ul className="svc-admin__rows">
              {rows.map((row) => (
                <li key={row.policy_id}>
                  <button type="button" className="svc-admin__row" data-active={selectedId === row.policy_id ? "true" : undefined} onClick={() => setSelectedId(row.policy_id)}>
                    <StatusBadge level={stateLevel(row.state)} label={stateLabel(row.state)} size="sm" />
                    <span className="svc-admin__row-subject"><strong>{row.policy_id}</strong><small>v{row.latest_version ?? "—"} · {row.assigned_agents} agent {row.tenant_default ? "· tenant default" : ""}</small></span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="svc-adm2__card" aria-label="Policy editor and versions">
          {selectedId === null ? (
            <div className="state-box" data-kind="empty"><strong>Select a policy</strong><span>Choose an enumerated policy or open one by exact ID.</span></div>
          ) : <PolicyWorkspace key={selectedId} policyId={selectedId} isAdmin={isAdmin} />}
        </section>
      </div>
    </div>
  );
}

function Stat(props: { readonly label: string; readonly value: string }): ReactNode {
  return <div className="svc-admin__stat"><span className="svc-admin__stat-value">{props.value}</span><span className="svc-admin__stat-label">{props.label}</span></div>;
}

function CreatePolicy(props: { readonly onCreated: (id: string) => void }): ReactNode {
  const client = useQueryClient();
  const [id, setId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setBusy(true); setError(null);
    const result = await controlPolicyAction<{ policy_id: string }>("POST", "policies", { policy_id: id.trim(), reason: reason.trim(), expectedVersion: 0 });
    setBusy(false);
    if (!result.ok) { setError(policyErrorMessage(result.code)); return; }
    await invalidatePolicies(client);
    props.onCreated(result.body.policy_id);
  };
  return (
    <form className="svc-adm2__card policy-admin__create" aria-label="Create policy" onSubmit={(event) => void submit(event)}>
      <div className="svc-adm2__card-head"><h3 className="svc-admin__h">Create an unassigned draft</h3><span className="svc-adm2__card-sub">lenient, audited save</span></div>
      <div className="policy-admin__form-grid">
        <div className="svc-admin__field"><label htmlFor="new-policy-id">Policy ID</label><input id="new-policy-id" value={id} maxLength={96} onChange={(event) => setId(event.target.value)} /></div>
        <div className="svc-admin__field"><label htmlFor="new-policy-reason">Reason (audit trail)</label><input id="new-policy-reason" value={reason} maxLength={256} onChange={(event) => setReason(event.target.value)} /></div>
      </div>
      {error !== null && <p className="svc-admin__err" role="alert">{error}</p>}
      <div className="svc-admin__actions"><button type="submit" className="svc-admin__btn" disabled={busy || !POLICY_ID.test(id.trim()) || reason.trim().length === 0}>{busy ? "Creating…" : "Create draft"}</button></div>
    </form>
  );
}

function PolicyWorkspace(props: { readonly policyId: string; readonly isAdmin: boolean }): ReactNode {
  const subpath = `policies/${encoded(props.policyId)}`;
  const detail = useServiceAdmin<PolicyDetailResponse>("ais", policyPath(subpath), [CONTROL_POLICY_ROOT, "policy", props.policyId]);
  const client = useQueryClient();
  const [draftText, setDraftText] = useState("");
  const [reason, setReason] = useState("");
  const [validation, setValidation] = useState<PolicyValidationResponse | null>(null);
  const [validating, setValidating] = useState(false);
  const [syntaxError, setSyntaxError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const loadedVersion = detail.data?.draft?.record_version;

  useEffect(() => {
    if (detail.data?.draft !== null && detail.data?.draft !== undefined) {
      setDraftText(JSON.stringify(detail.data.draft.draft, null, 2));
      setValidation(null); setSyntaxError(null); setNotice(null); setActionError(null);
    }
  }, [props.policyId, loadedVersion]);

  useEffect(() => {
    if (detail.data?.draft == null || draftText.length === 0) return;
    let parsed: unknown;
    try { parsed = JSON.parse(draftText); }
    catch { setSyntaxError("Draft JSON is not valid."); setValidation(null); setValidating(false); return; }
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      setSyntaxError("Draft JSON must be an object."); setValidation(null); return;
    }
    setSyntaxError(null); setValidating(true);
    const controller = new AbortController();
    let current = true;
    const timer = window.setTimeout(() => {
      void controlPolicyAction<PolicyValidationResponse>("POST", `${subpath}/validate`, { draft: parsed }, controller.signal).then((result) => {
        if (!current) return;
        setValidating(false);
        if (result.ok) setValidation(result.body);
        else setValidation({ ...EMPTY_VALIDATION, findings: [{ severity: "error", path: result.path ?? "$", rule: result.rule ?? policyErrorMessage(result.code), code: result.code }] });
      });
    }, 350);
    return () => { current = false; window.clearTimeout(timer); controller.abort(); };
  }, [draftText, subpath, detail.data?.draft]);

  const meter = useMemo(() => estimateSignedJwsChars(draftText), [draftText]);
  const latestVersion = detail.data?.versions.reduce((max, version) => Math.max(max, version.version), 0) ?? 0;

  const save = async (): Promise<void> => {
    let draft: unknown;
    try { draft = JSON.parse(draftText); } catch { setSyntaxError("Draft JSON is not valid."); return; }
    setSaving(true); setActionError(null); setNotice(null);
    const result = await controlPolicyAction<{ record_version: number }>("PUT", `${subpath}/draft`, {
      draft, reason: reason.trim(), expectedVersion: detail.data?.draft?.record_version,
    });
    setSaving(false);
    if (!result.ok) { setActionError(policyErrorMessage(result.code)); return; }
    setReason(""); setNotice(`Draft saved at CAS version ${result.body.record_version}. Publish has not occurred.`);
    await invalidatePolicies(client);
  };

  if (detail.isError) return <AvailabilityError error={detail.error} area={`policy ${props.policyId}`} />;
  if (detail.isPending || detail.data === undefined) return <div className="state-box" data-kind="loading" role="status"><strong>Loading policy…</strong><span>Fetching the draft and immutable versions.</span></div>;

  const versions = [...detail.data.versions].sort((a, b) => b.version - a.version);
  return (
    <div className="policy-workspace">
      <div className="svc-adm2__card-head"><h3 className="svc-admin__h">{props.policyId}</h3><span className="svc-adm2__card-sub">latest v{latestVersion || "—"}</span></div>
      {detail.data.draft === null ? (
        <div className="state-box" data-kind="empty"><strong>No current draft</strong><span>Create a new draft for this ID to publish another immutable version.</span></div>
      ) : (
        <section className="policy-editor" aria-label="Structured policy draft">
          <div className="policy-admin__section-head"><h4 className="svc-admin__section-title">Draft JSON</h4><span>CAS {detail.data.draft.record_version} · updated by {detail.data.draft.updated_by}</span></div>
          <label className="svc-admin__stat-sr" htmlFor="policy-draft-json">Policy draft JSON</label>
          <textarea id="policy-draft-json" className="policy-editor__json" rows={18} spellCheck={false} value={draftText} readOnly={!props.isAdmin} aria-invalid={syntaxError !== null} aria-describedby="policy-length-caption" onChange={(event) => { setDraftText(event.target.value); setNotice(null); }} />
          <JwsMeter estimate={meter} />
          {syntaxError !== null && <p className="svc-admin__err" role="alert">{syntaxError}</p>}
          <ValidationPanel validation={validation} validating={validating} />
          <div className="policy-admin__guidance" role="note">
            <p><strong>Operation classes:</strong> AIS uses read (read/list), create, update, delete, and unknown. Policy-layer write means every non-read; admin derives from capability.</p>
            <p><strong>Enforcement notes:</strong> <code>mode_hint</code> and <code>applies_to.environments</code> are metadata and are not enforced. <code>decision_mode: always</code> adds about 0.6 to 0.7 s per call so reads meet lifecycle checks.</p>
            <p><strong>Clearance:</strong> granted clearance is currently 0; risk-tier floor or overrides of 1+ generate warnings.</p>
          </div>
          {props.isAdmin && (
            <div className="policy-editor__save">
              <div className="svc-admin__field"><label htmlFor="draft-save-reason">Save reason (required, audited)</label><input id="draft-save-reason" value={reason} maxLength={256} onChange={(event) => setReason(event.target.value)} /></div>
              <div className="svc-admin__actions">
                <button type="button" className="svc-admin__btn" disabled={saving || reason.trim().length === 0 || syntaxError !== null} onClick={() => void save()}>{saving ? "Saving…" : "Save draft"}</button>
                <button type="button" className="svc-admin__btn" disabled={saving || syntaxError !== null || validation?.ok !== true || meter.level === "error"} onClick={() => setPublishOpen(true)}>Publish…</button>
              </div>
              <p className="svc-admin__muted">Draft save is lenient but reasoned and audited. It has no effect on tokens until the separate strict publish and assignment actions succeed.</p>
            </div>
          )}
          {notice !== null && <p className="policy-admin__success" role="status">{notice}</p>}
          {actionError !== null && <p className="svc-admin__err" role="alert">{actionError}</p>}
          <PublishDialog open={publishOpen} policyId={props.policyId} expectedVersion={latestVersion} validation={validation ?? EMPTY_VALIDATION} onClose={() => setPublishOpen(false)} onPublished={async (version) => { setPublishOpen(false); setNotice(`Published immutable version ${version}.`); await invalidatePolicies(client); }} />
        </section>
      )}

      <section aria-label="Published versions" className="policy-versions">
        <h4 className="svc-admin__section-title">Published versions</h4>
        {versions.length === 0 ? <p className="svc-admin__muted">No published versions.</p> : (
          <ul className="policy-versions__list">
            {versions.map((version) => <li key={version.version}><button type="button" className="svc-admin__btn" aria-pressed={selectedVersion === version.version} onClick={() => setSelectedVersion(version.version)}>v{version.version}</button><VersionSummary version={version} /></li>)}
          </ul>
        )}
        {selectedVersion !== null && <VersionDetail key={selectedVersion} policyId={props.policyId} version={selectedVersion} />}
      </section>
    </div>
  );
}

function JwsMeter(props: { readonly estimate: ReturnType<typeof estimateSignedJwsChars> }): ReactNode {
  const { estimate } = props;
  return (
    <div className="policy-meter" data-level={estimate.level}>
      <div className="policy-meter__label"><span>Estimated signed JWS length</span><strong>{estimate.characters.toLocaleString()} / {POLICY_JWS_LIMIT.toLocaleString()} characters</strong></div>
      <progress max={POLICY_JWS_LIMIT} value={Math.min(estimate.characters, POLICY_JWS_LIMIT)} aria-label="Estimated signed policy length" />
      <p id="policy-length-caption">Live conservative estimate includes signing overhead. Strict publish measures the exact compact JWS and refuses more than 65,536 characters. A token policy_ref is separately capped at 300 bytes.</p>
    </div>
  );
}

function ValidationPanel(props: { readonly validation: PolicyValidationResponse | null; readonly validating: boolean }): ReactNode {
  const findings = props.validation?.findings ?? [];
  return (
    <section className="policy-findings" aria-label="Validation findings" aria-live="polite">
      <div className="policy-admin__section-head"><h4 className="svc-admin__section-title">Validation findings</h4><span>{props.validating ? "Checking…" : props.validation === null ? "Waiting for valid JSON" : props.validation.ok ? "No blocking findings" : "Publish blocked"}</span></div>
      {findings.length === 0 ? <p className="svc-admin__muted">{props.validation?.ok === true ? "The AIS semantic validator found no issues." : "Findings appear after the draft is parsed and validated."}</p> : (
        <ul className="policy-findings__list">{findings.map((finding, index) => <Finding key={`${finding.path}-${finding.code ?? finding.rule}-${index}`} finding={finding} />)}</ul>
      )}
      {props.validation !== null && Object.keys(props.validation.operation_classes).length > 0 && (
        <details><summary>Operation classifications</summary><ul>{Object.entries(props.validation.operation_classes).map(([operation, classes]) => <li key={operation}><code>{operation}</code>: AIS {classes.aisClass}; policy {classes.policyClass}</li>)}</ul></details>
      )}
      {props.validation?.acknowledgement_required === true && <p className="policy-admin__warning"><strong>Acknowledgement required:</strong> unsupported-server findings must be explicitly acknowledged during strict publish.</p>}
    </section>
  );
}
function Finding(props: { readonly finding: PolicyFinding }): ReactNode {
  const level: StatusLevel = props.finding.severity === "error" ? "error" : props.finding.severity === "warning" ? "warn" : "unknown";
  return <li><StatusBadge level={level} label={props.finding.severity} size="sm" /><code>{props.finding.path}</code><span>{props.finding.rule}</span></li>;
}

function PublishDialog(props: { readonly open: boolean; readonly policyId: string; readonly expectedVersion: number; readonly validation: PolicyValidationResponse; readonly onClose: () => void; readonly onPublished: (version: number) => Promise<void> }): ReactNode {
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formId = useId();
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setBusy(true); setError(null);
    const result = await controlPolicyAction<{ version: number }>("POST", `policies/${encoded(props.policyId)}/publish`, {
      reason: reason.trim(), expectedVersion: props.expectedVersion, mfa_code: code,
      acknowledge: props.validation.acknowledgement_required ? ack : false,
      ...(reviewer.trim() === "" ? {} : { reviewer: reviewer.trim() }),
    });
    setBusy(false);
    if (!result.ok) { setError(policyErrorMessage(result.code)); if (result.code === "mfa_required") setCode(""); return; }
    await props.onPublished(result.body.version);
  };
  return (
    <Modal open={props.open} onClose={props.onClose} title={`Publish ${props.policyId}`} description={`Strict step-up action. The published version is immutable and uses published-head CAS ${props.expectedVersion}.`} footer={<><button type="button" className="svc-admin__btn" disabled={busy} onClick={props.onClose}>Cancel</button><button type="submit" form={formId} className="svc-admin__btn" disabled={busy || reason.trim() === "" || !/^\d{6}$/u.test(code) || (props.validation.acknowledgement_required && !ack)}>{busy ? "Publishing…" : "Publish immutable version"}</button></>}>
      <form id={formId} className="svc-admin__form" onSubmit={(event) => void submit(event)}>
        <div className="svc-admin__field"><label htmlFor={`${formId}-reason`}>Publish reason (audit trail)</label><textarea id={`${formId}-reason`} rows={3} maxLength={256} value={reason} onChange={(event) => setReason(event.target.value)} /></div>
        <div className="svc-admin__field"><label htmlFor={`${formId}-reviewer`}>Reviewer (when required)</label><input id={`${formId}-reviewer`} value={reviewer} maxLength={256} onChange={(event) => setReviewer(event.target.value)} /></div>
        <div className="svc-admin__field"><label htmlFor={`${formId}-mfa`}>Two-factor code</label><OtpInput id={`${formId}-mfa`} value={code} onChange={setCode} disabled={busy} align="start" /></div>
        {props.validation.acknowledgement_required && <label className="policy-admin__check"><input type="checkbox" checked={ack} onChange={(event) => setAck(event.target.checked)} /> I acknowledge that unsupported controls may produce policy_unsupported_version or policy_unsupported_control_member at MCP servers.</label>}
        {error !== null && <p className="svc-admin__err" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

function VersionSummary(props: { readonly version: PolicyVersionMeta }): ReactNode {
  return <span><code>{props.version.digest}</code><small>issued {secondsDate(props.version.issued_at)} · expires {secondsDate(props.version.expires_at)}</small></span>;
}
function VersionDetail(props: { readonly policyId: string; readonly version: number }): ReactNode {
  const query = useServiceAdmin<PolicyVersionResponse>("ais", policyPath(`policies/${encoded(props.policyId)}/versions/${props.version}`), [CONTROL_POLICY_ROOT, "policy", props.policyId, "version", props.version]);
  const [notice, setNotice] = useState<string | null>(null);
  if (query.isError) return <p className="svc-admin__err" role="alert">The immutable version could not be loaded.</p>;
  if (query.isPending || query.data === undefined) return <p className="svc-admin__muted" role="status">Loading immutable version…</p>;
  const data = query.data;
  const policyRef = JSON.stringify({ id: data.policy_id, version: data.version, digest: data.digest, exp: data.expires_at });
  const refBytes = new TextEncoder().encode(policyRef).byteLength;
  const copy = async (): Promise<void> => {
    try { await navigator.clipboard.writeText(data.digest); setNotice("Digest copied."); }
    catch { setNotice("Digest copy is unavailable in this browser."); }
  };
  const download = (): void => {
    const blob = new Blob([JSON.stringify({ policy_id: data.policy_id, version: data.version, digest: data.digest, kid: data.kid, issued_at: data.issued_at, expires_at: data.expires_at, supersedes: data.supersedes, jws: data.jws, payload: data.payload }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const link = document.createElement("a"); link.href = url; link.download = `${data.policy_id}-v${data.version}.signed.json`; link.click(); URL.revokeObjectURL(url); setNotice("Signed public policy downloaded. No private key material is included.");
  };
  return (
    <article className="policy-version-detail" aria-label={`Policy version ${data.version}`}>
      <dl className="svc-adm2__kv">
        <div className="svc-admin__kv-row"><dt>Version</dt><dd>{data.version} (immutable)</dd></div>
        <div className="svc-admin__kv-row"><dt>Digest</dt><dd><code>{data.digest}</code></dd></div>
        <div className="svc-admin__kv-row"><dt>Signing key ID</dt><dd><code>{data.kid}</code></dd></div>
        <div className="svc-admin__kv-row"><dt>Issued / expires</dt><dd>{secondsDate(data.issued_at)} / {secondsDate(data.expires_at)}</dd></div>
        <div className="svc-admin__kv-row"><dt>Supersedes</dt><dd>{data.supersedes ?? "First version"}</dd></div>
        <div className="svc-admin__kv-row"><dt>policy_ref size</dt><dd>{refBytes} / 300 bytes</dd></div>
        <div className="svc-admin__kv-row"><dt>Signed JWS length</dt><dd>{data.jws.length.toLocaleString()} / {POLICY_JWS_LIMIT.toLocaleString()} characters</dd></div>
      </dl>
      <div className="svc-admin__actions"><button type="button" className="svc-admin__btn" onClick={() => void copy()}>Copy digest</button><button type="button" className="svc-admin__btn" onClick={download}>Download signed policy</button></div>
      {notice !== null && <p className="svc-admin__muted" role="status">{notice}</p>}
      <details className="svc-admin__raw"><summary>Decoded public payload</summary><pre>{JSON.stringify(data.payload, null, 2)}</pre></details>
    </article>
  );
}

interface EditableAssignment { readonly key: string; readonly scope: "tenant" | "agent"; readonly scope_key: string; readonly policy_id: string; readonly pinned_version: number | null }
let assignmentKey = 0;
function editable(row: AssignmentRow): EditableAssignment { return { ...row, key: `assignment-${assignmentKey++}` }; }

/**
 * Agent-scope assignments match the BARE stored agent sub (for example `northwind-reporter.1a2b3c4d5e6f`). A full
 * SPIFFE ID or a legacy `oathera:` ID is accepted by the API but never matches, so the editor warns about it.
 */
function looksLikeFullAgentId(value: string): boolean {
  const v = value.trim();
  return v.startsWith("spiffe://") || v.startsWith("oathera:") || v.includes("/agent/");
}

/** Whole-set assignment editor, resolver preview, coverage, precedence, and standing delay notice. */
export function ControlPolicyAssignments(): ReactNode {
  const isAdmin = useOptionalAuth()?.identity?.role === "admin";
  const assignments = useServiceAdmin<AssignmentsResponse>("ais", policyPath("assignments"), [CONTROL_POLICY_ROOT, "assignments"]);
  const coverage = useServiceAdmin<CoverageResponse>("ais", policyPath("coverage"), [CONTROL_POLICY_ROOT, "coverage"]);
  const policies = useServiceAdmin<PolicyListResponse>("ais", policyPath("policies"), [CONTROL_POLICY_ROOT, "policies"]);
  const client = useQueryClient();
  const [rows, setRows] = useState<EditableAssignment[]>([]);
  const [commitOpen, setCommitOpen] = useState(false);
  const [unassignment, setUnassignment] = useState<string | null>(null);
  const recordVersion = assignments.data?.record_version;
  useEffect(() => { if (assignments.data !== undefined) setRows(assignments.data.assignments.map(editable)); }, [recordVersion]);

  if (assignments.isError) return <AvailabilityError error={assignments.error} area="assignments" />;
  return (
    <div className="svc-admin svc-admin--full policy-admin">
      <aside className="policy-admin__delay" role="note">
        <strong>Standing assignment-change delay.</strong> The maximum delay is the lifetime of the last token issued before the change plus the 5 s verifier tolerance: <strong>305 s</strong>, and <strong>65 s</strong> when the token carries a privileged capability, plus the commit-to-token lag. The resolver uses D1 primary reads with no AIS cache; commit-to-token lag is unverified.
      </aside>
      <aside className="policy-admin__notice" role="note"><strong>Resolution never merges assignments.</strong> Agent scope wins over tenant scope, and exactly one policy resolves—or none. Changes apply to new tokens only.</aside>
      {!isAdmin && <p className="svc-admin__muted">Read-only view. Only a tenant administrator can commit the whole assignment set.</p>}

      <section className="svc-adm2__card" aria-label="Policy assignments">
        <div className="svc-adm2__card-head"><h3 className="svc-admin__h">Assignments</h3><span className="svc-adm2__card-sub">whole-set CAS {recordVersion ?? "…"}</span></div>
        {assignments.isPending ? <div className="state-box" data-kind="loading" role="status"><strong>Loading assignments…</strong><span>Fetching the current whole set.</span></div> : (
          <>
            {rows.length === 0 ? <div className="state-box" data-kind="empty"><strong>No assignments</strong><span>New tokens carry no policy_ref and no policy-derived controls.</span></div> : (
              <div className="policy-assignments" role="group" aria-label="Editable assignment set">
                {rows.map((row, index) => <AssignmentEditor key={row.key} row={row} index={index} disabled={!isAdmin} policyIds={policies.data?.policies.map((item) => item.policy_id) ?? []} onChange={(next) => setRows((current) => current.map((item) => item.key === row.key ? next : item))} onRemove={() => { setRows((current) => current.filter((item) => item.key !== row.key)); setUnassignment("Removing an assignment disables controls for new tokens when no agent or tenant fallback remains. Existing pre-change tokens can remain effective for the standing delay above."); }} />)}
              </div>
            )}
            {unassignment !== null && <p className="policy-admin__warning" role="alert">{unassignment}</p>}
            {isAdmin && <div className="svc-admin__actions"><button type="button" className="svc-admin__btn" onClick={() => setRows((current) => [...current, { key: `assignment-${assignmentKey++}`, scope: "agent", scope_key: "", policy_id: "", pinned_version: null }])}>Add assignment</button><button type="button" className="svc-admin__btn" onClick={() => setCommitOpen(true)} disabled={assignments.isPending}>Review strict commit…</button></div>}
            <p className="svc-admin__muted">Latest always resolves the highest published version; it never silently falls back. Explicit pins must equal the highest published, non-expired version.</p>
          </>
        )}
      </section>
      <AssignmentPreviewPanel />
      <CoveragePanel query={coverage} />
      <AssignmentCommitDialog open={commitOpen} expectedVersion={recordVersion ?? 0} rows={rows} onClose={() => setCommitOpen(false)} onCommitted={async () => { setCommitOpen(false); setUnassignment(null); await invalidatePolicies(client); }} />
    </div>
  );
}

function AssignmentEditor(props: { readonly row: EditableAssignment; readonly index: number; readonly disabled: boolean; readonly policyIds: readonly string[]; readonly onChange: (row: EditableAssignment) => void; readonly onRemove: () => void }): ReactNode {
  const prefix = `assignment-${props.index}`;
  const update = (change: Partial<EditableAssignment>): void => props.onChange({ ...props.row, ...change });
  return (
    <fieldset className="policy-assignment" disabled={props.disabled}>
      <legend>Assignment {props.index + 1}</legend>
      <div className="svc-admin__field"><label htmlFor={`${prefix}-scope`}>Scope</label><select id={`${prefix}-scope`} value={props.row.scope} onChange={(event) => update({ scope: event.target.value as "tenant" | "agent", scope_key: event.target.value === "tenant" ? "" : props.row.scope_key })}><option value="tenant">Tenant default</option><option value="agent">Agent override</option></select></div>
      <div className="svc-admin__field"><label htmlFor={`${prefix}-key`}>Agent sub</label><input id={`${prefix}-key`} value={props.row.scope_key} disabled={props.disabled || props.row.scope === "tenant"} placeholder={props.row.scope === "tenant" ? "All current and future agents" : "Bare agent sub, e.g. northwind-reporter.1a2b3c4d5e6f"} onChange={(event) => update({ scope_key: event.target.value })} />{props.row.scope === "agent" && looksLikeFullAgentId(props.row.scope_key) && <p className="policy-admin__warning">Use the bare agent sub (the part after <code>/agent/</code>), not the full SPIFFE or legacy ID. A full ID is accepted but never matches an agent.</p>}</div>
      <div className="svc-admin__field"><label htmlFor={`${prefix}-policy`}>Policy ID</label><input id={`${prefix}-policy`} list={`${prefix}-policy-options`} value={props.row.policy_id} onChange={(event) => update({ policy_id: event.target.value })} /></div>
      <div className="svc-admin__field"><label htmlFor={`${prefix}-pin`}>Version choice</label><select id={`${prefix}-pin`} value={props.row.pinned_version === null ? "latest" : "pinned"} onChange={(event) => update({ pinned_version: event.target.value === "latest" ? null : 1 })}><option value="latest">Latest</option><option value="pinned">Explicit version</option></select></div>
      {props.row.pinned_version !== null && <div className="svc-admin__field"><label htmlFor={`${prefix}-version`}>Pinned version</label><input id={`${prefix}-version`} type="number" min={1} step={1} value={props.row.pinned_version} onChange={(event) => update({ pinned_version: Math.max(1, Number(event.target.value) || 1) })} /></div>}
      {!props.disabled && <button type="button" className="svc-admin__btn svc-admin__btn--danger" onClick={props.onRemove}>Unassign</button>}
      <datalist id={`${prefix}-policy-options`}>{props.policyIds.map((id) => <option key={id} value={id} />)}</datalist>
    </fieldset>
  );
}

function AssignmentPreviewPanel(): ReactNode {
  const [agent, setAgent] = useState("");
  const [preview, setPreview] = useState<AssignmentPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setBusy(true); setError(null);
    const result = await controlPolicyAction<AssignmentPreview>("POST", "assignments/preview", { agent_sub: agent.trim() });
    setBusy(false);
    if (!result.ok) { setError(policyErrorMessage(result.code)); setPreview(null); return; }
    setPreview(result.body);
  };
  return (
    <section className="svc-adm2__card" aria-label="Assignment resolver preview">
      <div className="svc-adm2__card-head"><h3 className="svc-admin__h">Resolver preview</h3><span className="svc-adm2__card-sub">same resolver as token issuance</span></div>
      <form className="policy-admin__direct" onSubmit={(event) => void submit(event)}><label htmlFor="preview-agent-sub">Agent sub (blank previews tenant default)</label><input id="preview-agent-sub" value={agent} placeholder="Bare agent sub, e.g. northwind-reporter.1a2b3c4d5e6f" onChange={(event) => setAgent(event.target.value)} /><button type="submit" className="svc-admin__btn" disabled={busy}>{busy ? "Resolving…" : "Preview"}</button></form>
      <p className="svc-admin__muted">Previews the committed assignment set only; uncommitted edits above are not included. Enter the bare agent sub.</p>
      {error !== null && <p className="svc-admin__err" role="alert">{error}</p>}
      {preview !== null && <p className="policy-admin__success" role="status">{preview.kind === "ok" ? `Resolves exactly ${preview.policyId} v${preview.version}; expires ${secondsDate(preview.exp)}.` : preview.kind === "none" ? "No policy resolves. New tokens carry no policy_ref." : `The resolved policy is unusable (${preview.reason}); token issuance fails closed.`}</p>}
    </section>
  );
}

function CoveragePanel(props: { readonly query: UseQueryResult<CoverageResponse> }): ReactNode {
  const data = props.query.data;
  const rows = data === undefined ? [] : [...data.noPolicy, ...data.expiringOrExpired];
  return (
    <section className="svc-adm2__card" aria-label="Policy coverage and warnings">
      <div className="svc-adm2__card-head"><h3 className="svc-admin__h">Coverage and warnings</h3><span className="svc-adm2__card-sub">{data === undefined ? "…" : `${data.noPolicyCount + data.expiringOrExpiredCount} affected agents`}</span></div>
      {props.query.isError ? <p className="svc-admin__err" role="alert">Coverage could not be loaded.</p> : props.query.isPending ? <div className="state-box" data-kind="loading" role="status"><strong>Loading coverage…</strong><span>Resolving policies for active agents.</span></div> : rows.length === 0 ? <div className="state-box" data-kind="empty"><strong>All agents covered</strong><span>No active agent is uncovered, expired, or expiring within seven days.</span></div> : (
        <div className="policy-admin__table-wrap"><table className="svc-adm2__table"><caption className="svc-admin__stat-sr">Agents with policy coverage warnings</caption><thead><tr><th scope="col">Agent sub</th><th scope="col">Agent status</th><th scope="col">Warning</th></tr></thead><tbody>{rows.map((row) => <CoverageTableRow key={`${row.agent_sub}-${row.reason}`} row={row} />)}</tbody></table></div>
      )}
      <div className="policy-admin__guidance" role="note"><p>Unsupported-server findings require acknowledgement. Invalid or too-large policies cannot publish. Version and assignment conflicts require reload and review; they never cause assignment merging.</p></div>
    </section>
  );
}
function CoverageTableRow(props: { readonly row: CoverageRow }): ReactNode {
  const label = props.row.reason === "no_assignment" ? "No assignment" : props.row.reason === "expired" ? "Expired" : "Expiring within 7 days";
  return <tr><td><code>{props.row.agent_sub}</code></td><td>{props.row.status}</td><td><StatusBadge level={props.row.reason === "expired" ? "error" : "warn"} label={label} size="sm" /></td></tr>;
}

function AssignmentCommitDialog(props: { readonly open: boolean; readonly expectedVersion: number; readonly rows: readonly EditableAssignment[]; readonly onClose: () => void; readonly onCommitted: () => Promise<void> }): ReactNode {
  const formId = useId();
  const [reason, setReason] = useState(""); const [code, setCode] = useState(""); const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  const invalid = props.rows.some((row) => row.policy_id.trim() === "" || (row.scope === "agent" && row.scope_key.trim() === ""));
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setBusy(true); setError(null);
    const result = await controlPolicyAction<AssignmentsResponse>("PUT", "assignments", { reason: reason.trim(), mfa_code: code, expectedVersion: props.expectedVersion, assignments: props.rows.map((row) => ({ scope: row.scope, scopeKey: row.scope === "tenant" ? "" : row.scope_key.trim(), policy_id: row.policy_id.trim(), pinnedVersion: row.pinned_version })) });
    setBusy(false);
    if (!result.ok) { setError(policyErrorMessage(result.code)); if (result.code === "mfa_required") setCode(""); return; }
    await props.onCommitted();
  };
  return (
    <Modal open={props.open} onClose={props.onClose} role="alertdialog" title="Commit the whole assignment set" description={`Strict step-up action using assignment CAS ${props.expectedVersion}. This replaces the complete set; removed rows are unassigned.`} footer={<><button type="button" className="svc-admin__btn" disabled={busy} onClick={props.onClose}>Cancel</button><button type="submit" form={formId} className="svc-admin__btn" disabled={busy || invalid || reason.trim() === "" || !/^\d{6}$/u.test(code)}>{busy ? "Committing…" : "Commit assignments"}</button></>}>
      <form id={formId} className="svc-admin__form" onSubmit={(event) => void submit(event)}><p className="policy-admin__warning">Unassignment disables controls for new tokens where no fallback resolves. Agent assignments override—not merge with—the tenant default.</p><div className="svc-admin__field"><label htmlFor={`${formId}-reason`}>Commit reason (audit trail)</label><textarea id={`${formId}-reason`} rows={3} maxLength={256} value={reason} onChange={(event) => setReason(event.target.value)} /></div><div className="svc-admin__field"><label htmlFor={`${formId}-mfa`}>Two-factor code</label><OtpInput id={`${formId}-mfa`} value={code} onChange={setCode} disabled={busy} align="start" /></div>{error !== null && <p className="svc-admin__err" role="alert">{error}</p>}</form>
    </Modal>
  );
}
